import React, { JSX } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { YouTubeAreaIconLink } from 'components/YouTubeAreaIconLink'
import { YouTubeTermsFooter } from 'components/YouTubeSessionWidget'
import { EventDetail, EventYouTubeForeignSource, YouTubeChatSession } from 'types/api'

export const STREAM_YT_HOST_FLAG_LABEL = 'This stream\'s YouTube channel (the host\'s)'
export const STREAM_YT_NOT_ATTACHED = 'The host has not attached a YouTube channel to this stream. The host can do that on the Integrations page.'
export const STREAM_YT_HELP = 'Everyone in this stream sees these YouTube messages on this stream\'s overlays. Only the host can start or stop the host\'s YouTube chat.'
export const STREAM_YT_WALL_PICKER = 'Pick the YouTube chats this wall shows.'
export const STREAM_YT_WALL_EMPTY = 'No YouTube chat is known for this stream yet. The host\'s channel shows here after its chat has run in this stream, or when the host opens this page.'

export const staffSourceLabel = (title: string): string => `${title} (added by FlightDeck staff)`

export interface KnownStreamChannel {
  channelId: string
  title: string
  staff: boolean
}

const titleOf = (title: string | null | undefined, channelId: string): string =>
  typeof title === 'string' && title.trim() !== '' ? title : channelId

const foreignOf = (event: Pick<EventDetail, 'youtubeForeignSources'>): EventYouTubeForeignSource[] =>
  (event.youtubeForeignSources ?? []).filter((f) => typeof f.channelId === 'string' && f.channelId !== '')

export const knownStreamYouTubeChannels = ({
  event,
  sessions,
  hostOwn
}: {
  event: Pick<EventDetail, 'youtubeForeignSources'>
  sessions: readonly YouTubeChatSession[]
  hostOwn: { channelId: string | null, channelTitle: string | null } | null
}): KnownStreamChannel[] => {
  const out: KnownStreamChannel[] = []
  const seen = new Set<string>()
  const add = (channelId: string | null | undefined, title: string | null | undefined, staff: boolean): void => {
    if (typeof channelId !== 'string' || channelId === '' || seen.has(channelId)) return
    seen.add(channelId)
    out.push({ channelId, title: titleOf(title, channelId), staff })
  }
  foreignOf(event).forEach((f) => add(f.channelId, f.channelTitle, true))
  sessions.forEach((s) => add(s.channelId, s.channelTitle, s.foreignEvent === true))
  if (hostOwn != null) add(hostOwn.channelId, hostOwn.channelTitle, false)
  return out
}

const checkboxClass = 'size-4 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand disabled:cursor-not-allowed disabled:opacity-60'
const rowClass = 'flex items-center gap-2.5 rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2 text-sm'

const Label = (): JSX.Element => (
  <span className='mb-1.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-dm-text-accent'>
    <YouTubeAreaIconLink size='sm' />
    YouTube
  </span>
)

const OtherRow = ({ channelId, title, onRemove }: { channelId: string, title: string, onRemove: () => void }): JSX.Element => (
  <li data-testid='stream-youtube-other-row' className={rowClass}>
    <span className='min-w-0 flex-1 truncate text-dm-text-primary' title={channelId}>{title}</span>
    <button
      type='button'
      onClick={onRemove}
      className='inline-flex shrink-0 items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-[11px] font-medium text-dm-text-secondary hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-300'
    >
      <XMarkIcon className='size-3.5' aria-hidden='true' />
      Remove
    </button>
  </li>
)

const toggle = (list: string[], channelId: string, on: boolean): string[] =>
  on ? (list.includes(channelId) ? list : [...list, channelId]) : list.filter((c) => c !== channelId)

export const StreamChatYouTubeSources = ({
  event,
  hostFlag,
  onHostFlagChange,
  channels,
  onChannelsChange
}: {
  event: Pick<EventDetail, 'youtubeSourceAttached' | 'youtubeForeignSources'>
  hostFlag: boolean
  onHostFlagChange: (on: boolean) => void
  channels: string[]
  onChannelsChange: (next: string[]) => void
}): JSX.Element => {
  const attached = event.youtubeSourceAttached === true
  const foreign = foreignOf(event)
  const foreignIds = new Set(foreign.map((f) => f.channelId))
  const others = channels.filter((c) => !foreignIds.has(c))
  return (
    <div data-testid='stream-youtube-sources'>
      <Label />
      <ul className='space-y-1.5'>
        <li>
          <label className={`${rowClass} cursor-pointer`}>
            <input
              type='checkbox'
              checked={hostFlag}
              disabled={!attached && !hostFlag}
              onChange={(e) => onHostFlagChange(e.target.checked)}
              data-testid='stream-youtube-host-flag'
              className={checkboxClass}
            />
            <span className='min-w-0 text-dm-text-primary'>{STREAM_YT_HOST_FLAG_LABEL}</span>
          </label>
        </li>
        {foreign.map((f) => (
          <li key={f.channelId}>
            <label className={`${rowClass} cursor-pointer`}>
              <input
                type='checkbox'
                checked={channels.includes(f.channelId)}
                onChange={(e) => onChannelsChange(toggle(channels, f.channelId, e.target.checked))}
                data-testid='stream-youtube-staff-source'
                className={checkboxClass}
              />
              <span className='min-w-0 truncate text-dm-text-primary' title={f.channelId}>
                {staffSourceLabel(titleOf(f.channelTitle, f.channelId))}
              </span>
            </label>
          </li>
        ))}
        {others.map((c) => (
          <OtherRow key={c} channelId={c} title={c} onRemove={() => onChannelsChange(toggle(channels, c, false))} />
        ))}
      </ul>
      {!attached && (
        <p className='mt-1 text-[11px] text-dm-text-accent' data-testid='stream-youtube-not-attached'>
          {STREAM_YT_NOT_ATTACHED}
        </p>
      )}
      <p className='mt-1 text-[11px] text-dm-text-accent' data-testid='stream-youtube-help'>
        {STREAM_YT_HELP}
      </p>
      <YouTubeTermsFooter />
    </div>
  )
}

export const StreamWallYouTubeSources = ({
  known,
  channels,
  onChannelsChange
}: {
  known: KnownStreamChannel[]
  channels: string[]
  onChannelsChange: (next: string[]) => void
}): JSX.Element => {
  const knownIds = new Set(known.map((k) => k.channelId))
  const others = channels.filter((c) => !knownIds.has(c))
  return (
    <div data-testid='stream-youtube-sources'>
      <Label />
      <p className='mb-2 text-[11px] text-dm-text-secondary'>{STREAM_YT_WALL_PICKER}</p>
      {known.length === 0 && (
        <p className='mb-2 rounded-md border border-white/5 bg-dm-bg-accent/20 px-3 py-2 text-[11px] leading-relaxed text-dm-text-secondary' data-testid='stream-youtube-wall-empty'>
          {STREAM_YT_WALL_EMPTY}
        </p>
      )}
      <ul className='space-y-1.5'>
        {known.map((k) => (
          <li key={k.channelId}>
            <label className={`${rowClass} cursor-pointer`}>
              <input
                type='checkbox'
                checked={channels.includes(k.channelId)}
                onChange={(e) => onChannelsChange(toggle(channels, k.channelId, e.target.checked))}
                data-testid='stream-youtube-wall-channel'
                className={checkboxClass}
              />
              <span className='min-w-0 truncate text-dm-text-primary' title={k.channelId}>
                {k.staff ? staffSourceLabel(k.title) : k.title}
              </span>
            </label>
          </li>
        ))}
        {others.map((c) => (
          <OtherRow key={c} channelId={c} title={c} onRemove={() => onChannelsChange(toggle(channels, c, false))} />
        ))}
      </ul>
      <YouTubeTermsFooter />
    </div>
  )
}
